import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquareQuote } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { PageHeader, SectionHeader } from '../components/Shared';
import { NextStepBand } from '../components/NextStepBand';
import { setPageMeta } from '../lib/seo';
import { ReviewCard, FacebookReviewsButton, useApprovedReviews } from '../components/ReviewBlocks';

export default function Reviews() {
  const { reviews, total, loading, error } = useApprovedReviews(200);

  useEffect(() => {
    setPageMeta({
      title: 'Reviews and Testimonials | Steve Lesperance, Ward 8',
      description: 'What residents, colleagues and clients say about working with Steve Lesperance. Read approved website reviews or add your own on Facebook.',
      url: `${window.location.origin}/reviews`,
    });
  }, []);

  const featured = reviews.filter((r) => r.featured);
  const others = reviews.filter((r) => !r.featured);

  return (
    <div>
      <PageHeader
        pageKey="reviews"
        kicker="REVIEWS AND TESTIMONIALS"
        title="What people say about working with Steve"
        subtitle="Real experiences from residents, colleagues and clients. Every website review is checked before it is published."
      />

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14" data-testid="reviews-page">
        <div className="flex flex-col sm:flex-row gap-3 mb-8">
          <Button asChild className="h-12 px-6 bg-[#1a2744] hover:bg-[#24406f] text-white font-bold btn-press justify-center" data-testid="reviews-leave-review-button">
            <Link to="/leave-a-review"><MessageSquareQuote className="h-4 w-4 mr-2" /> Leave a Website Review</Link>
          </Button>
          <FacebookReviewsButton className="border-2 border-[#1a2744] text-[#1a2744]" testId="reviews-facebook-reviews-button" />
        </div>

        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="reviews-loading">
            {[0, 1, 2].map((i) => <Skeleton key={i} className="h-52 rounded-xl" />)}
          </div>
        )}
        {!loading && error && (
          <Card className="border-amber-200 bg-amber-50" data-testid="reviews-error">
            <CardContent className="p-5">
              <p className="font-serif font-bold text-amber-900">Reviews could not be loaded right now.</p>
              <p className="text-sm text-amber-900 mt-1">Please try again in a moment, or read reviews on Facebook.</p>
            </CardContent>
          </Card>
        )}
        {!loading && !error && reviews.length === 0 && (
          <Card data-testid="reviews-empty">
            <CardContent className="p-8 text-center">
              <MessageSquareQuote className="h-8 w-8 text-[#C9A84C] mx-auto" />
              <p className="font-serif font-bold text-[#1a2744] text-lg mt-3">No website reviews have been published yet.</p>
              <p className="text-sm text-slate-600 mt-1">Know Steve or have worked with him? Be the first to share your experience.</p>
            </CardContent>
          </Card>
        )}
        {!loading && featured.length > 0 && (
          <div className="mb-10" data-testid="reviews-featured">
            <SectionHeader kicker="FEATURED" title="Featured reviews" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {featured.map((r) => <ReviewCard key={r.id} review={r} />)}
            </div>
          </div>
        )}
        {!loading && others.length > 0 && (
          <div data-testid="reviews-all">
            <SectionHeader kicker="ALL REVIEWS" title={`${total} review${total === 1 ? '' : 's'}`} />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {others.map((r) => <ReviewCard key={r.id} review={r} />)}
            </div>
          </div>
        )}
        <p className="text-xs text-slate-500 mt-8" data-testid="reviews-disclosure">Reviews are submitted by the people named, with their consent to publish. Website reviews are separate from Facebook reviews and are not posted to Facebook automatically.</p>
      </section>

      <NextStepBand
        title="Have your own experience to share?"
        buttonLabel="Leave a Website Review"
        to="/leave-a-review"
        secondaryLabel="Or see the ways to get involved"
        secondaryTo="/get-involved"
        testId="reviews-next-step"
      />
    </div>
  );
}
